// PERFORMANCE OPTIMIZATION ADDED - React.memo and useMemo for better performance
import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X, User, ShoppingCart, Shield, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { adminMenuItems } from "../utils/adminMenuItems";

const Navbar: React.FC = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const { role, isAuthenticated, logout } = useAuth();
  const { getCartItemCount } = useCart();
  const cartItemCount = getCartItemCount();

  // UI/UX REFRESH - Page scroll progress, spring-smoothed and rendered with a GPU-friendly scaleX
  const { scrollYProgress } = useScroll();
  const progressScaleX = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 30,
    mass: 0.3,
  });

  // React 19: Ref callback with cleanup function
  const handleNavRef = (element: HTMLElement | null) => {
    if (element) {
      navRef.current = element;
    }

    // Return cleanup function
    return () => {
      if (navRef.current) {
        navRef.current = null;
      }
    };
  };

  // PERFORMANCE OPTIMIZATION ADDED - useMemo to memoize navItems array
  const navItems = useMemo(() => [
    ...(role === "user" || role === "public"
      ? [
          { name: "Buy Mangoes", path: "/mangoes" },
          { name: "About Us ", path: "/about" },
          // { name: "Gallery", path: "/gallery" },
          { name: "Contact", path: "/contact" },
          { name: "Orders", path: "/orders" },
        ]
      : []),
  ], [role]);

  // PERFORMANCE OPTIMIZATION ADDED - useCallback for scroll handler
  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    // UI/UX REFRESH - Passive listener keeps scrolling smooth
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // PERFORMANCE OPTIMIZATION ADDED - useCallback for toggle menu
  const toggleMenu = useCallback(() => setIsOpen(prev => !prev), []);

  const isAdminRoute = location.pathname.startsWith("/admin");
  const shouldUseAdminStyle = role === "admin" || isAdminRoute;

  // PERFORMANCE OPTIMIZATION ADDED - useCallback for logout handler
  const handleLogout = useCallback(() => {
    logout();
    setIsOpen(false);
  }, [logout]);

  return (
    <motion.nav
      ref={handleNavRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        shouldUseAdminStyle
          ? "bg-white shadow-lg"
          : scrolled
          ? "bg-white/85 backdrop-blur-xl backdrop-saturate-150 shadow-md"
          : "bg-white"
      }`}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        willChange: "transform",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
      }}
      aria-label="Main navigation"
    >
      <div className="w-full max-w-full mx-auto px-3 sm:px-4" style={{ overflowX: "hidden" }}>
        <div className="flex items-center justify-between h-16 min-w-0 gap-2">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-shrink-0"
            style={{ flexShrink: 0 }}
          >
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="flex-shrink-0"
            >
              <img 
                src="/favicon.png" 
                alt="Golden Harvest Mango Farm Logo" 
                className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 flex-shrink-0 object-contain"
                width="32"
                height="32"
                loading="eager"
              />
            </motion.div>
            <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gradient whitespace-nowrap font-logo tracking-wide" style={{ letterSpacing: '0.05em' }}>
              Golden Harvest Mango
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6 xl:space-x-8 flex-shrink-0">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative py-1 font-medium transition-colors duration-300 whitespace-nowrap ${
                  location.pathname === item.path
                    ? "text-mango-600"
                    : "text-gray-700 hover:text-mango-600 after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:rounded-full after:bg-mango-300 after:transition-transform after:duration-300 hover:after:scale-x-100"
                }`}
              >
                {item.name}
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-gradient-to-r from-mango-500 to-golden-500"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </Link>
            ))}

            {/* Account and Cart Icons */}
            {role !== "admin" && (
              <div className="flex items-center space-x-4 ml-4 pl-4 border-l border-gray-300 flex-shrink-0">
                <Link
                  to={isAuthenticated ? "/profile" : "/login"}
                  className="relative p-2 rounded-xl hover:bg-mango-50 active:scale-95 transition group flex-shrink-0"
                  aria-label={isAuthenticated ? "View profile" : "Login"}
                >
                  <User className="h-5 w-5 md:h-6 md:w-6 text-golden-600 group-hover:text-mango-600 transition duration-300 group-hover:scale-110" aria-hidden="true" />
                </Link>
                <Link
                  to="/cart"
                  className="relative p-2 rounded-xl hover:bg-mango-50 active:scale-95 transition group flex-shrink-0"
                  aria-label={`Shopping cart${cartItemCount > 0 ? ` with ${cartItemCount} item${cartItemCount !== 1 ? 's' : ''}` : ''}`}
                >
                  <ShoppingCart className="h-5 w-5 md:h-6 md:w-6 text-golden-600 group-hover:text-mango-600 transition duration-300 group-hover:scale-110" aria-hidden="true" />
                  {cartItemCount > 0 && (
                    <motion.span
                      key={cartItemCount}
                      initial={{ scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 520, damping: 18 }}
                      className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 bg-gradient-to-br from-red-500 to-rose-600 text-white text-[10px] md:text-xs font-bold rounded-full w-4 h-4 md:w-5 md:h-5 flex items-center justify-center leading-none ring-2 ring-white shadow-sm" aria-label={`${cartItemCount} items in cart`}>
                      {cartItemCount > 99 ? "99+" : cartItemCount}
                    </motion.span>
                  )}
                </Link>
              </div>
            )}

            {/* Admin Badge */}
            {role === "admin" && (
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-300 flex-shrink-0">
                <Shield className="h-4 w-4 md:h-5 md:w-5 text-golden-600" />
                <span className="text-sm md:text-base lg:text-lg font-semibold text-gray-900">Admin</span>
              </div>
            )}
          </div>

          {/* Mobile Cart and Menu Button */}
          <div className="md:hidden flex items-center gap-2 flex-shrink-0">
            {role !== "admin" && role === "user" && (
              <Link
                to="/cart"
                className="relative p-2 rounded-xl hover:bg-mango-50 active:scale-95 transition flex-shrink-0"
                aria-label={`Shopping cart${cartItemCount > 0 ? ` with ${cartItemCount} item${cartItemCount !== 1 ? 's' : ''}` : ''}`}
              >
                <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-golden-600" aria-hidden="true" />
                {cartItemCount > 0 && (
                  <motion.span
                    key={cartItemCount}
                    initial={{ scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 520, damping: 18 }}
                    className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-gradient-to-br from-red-500 to-rose-600 text-white text-[10px] sm:text-xs font-bold rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center leading-none ring-2 ring-white shadow-sm" aria-label={`${cartItemCount} items in cart`}>
                    {cartItemCount > 99 ? "99+" : cartItemCount}
                  </motion.span>
                )}
              </Link>
            )}
            <button
              onClick={toggleMenu}
              className="p-2 rounded-xl hover:bg-mango-50 active:scale-95 transition flex-shrink-0"
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
              style={{ flexShrink: 0 }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={isOpen ? "close" : "open"}
                  initial={{ opacity: 0, rotate: isOpen ? -90 : 90, scale: 0.7 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: isOpen ? 90 : -90, scale: 0.7 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  className="flex"
                >
                  {isOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6 text-golden-600" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6 text-golden-600" />}
                </motion.span>
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={
            isOpen ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }
          }
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="md:hidden overflow-hidden bg-gradient-to-br from-mango-50 via-farm-50 to-golden-50 w-full"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fbbf24' fill-opacity='0.08'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            overflowX: "hidden",
          }}
        >
          <div className="py-3">
            {/* Show admin menu items when on admin routes */}
            {isAdminRoute && role === "admin" ? (
              <>
                <div className="grid grid-cols-2 gap-2 px-2">
                  {adminMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-300 ${
                          isActive
                            ? "bg-mango-100 text-mango-600"
                            : "text-gray-700 hover:bg-gray-100 hover:text-mango-500"
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="text-xs sm:text-sm">{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
                <div className="px-2 pt-3 border-t border-gray-200 mt-3">
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors duration-300 w-full"
                  >
                    <LogOut className="h-4 w-4 flex-shrink-0" />
                    <span className="text-xs sm:text-sm">Logout</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Regular navigation items */}
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={false}
                    animate={isOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: -12 }}
                    transition={{ duration: 0.3, delay: isOpen ? 0.05 + index * 0.05 : 0, ease: [0.22, 1, 0.36, 1] }}
                  >
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block mx-2 px-4 py-2.5 rounded-xl font-medium transition duration-300 active:scale-[0.98] ${
                      location.pathname === item.path
                        ? "bg-mango-100 text-mango-700 shadow-sm"
                        : "text-gray-700 hover:bg-white/80 hover:text-mango-600 hover:translate-x-1"
                    }`}
                  >
                    {item.name}
                  </Link>
                  </motion.div>
                ))}
                {role !== "admin" && (
                  <div className="flex items-center justify-center sm:justify-start gap-3 sm:gap-4 px-4 pt-3 sm:pt-4 border-t border-gray-200">
                    <Link
                      to={isAuthenticated ? "/profile" : "/login"}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center space-x-1.5 sm:space-x-2 text-gray-700 hover:text-mango-500 transition-colors py-1"
                    >
                      <User className="h-4 w-4 sm:h-5 sm:w-5 text-golden-600 flex-shrink-0" />
                      <span className="text-xs sm:text-sm md:text-base">My Account</span>
                    </Link>
                  </div>
                )}
                {role === "admin" && !isAdminRoute && (
                  <div className="flex items-center gap-2 px-4 pt-4 border-t border-gray-200">
                    <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-golden-600" />
                    <span className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">Admin</span>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>

      {/* Golden line decoration */}
      <div className="relative h-0.5 bg-gradient-to-r from-golden-400 via-golden-300 to-farm-200 w-full" aria-hidden="true" style={{ marginBottom: 0 }}>
        {/* UI/UX REFRESH - Scroll progress indicator drawn over the golden line */}
        <motion.div
          className="absolute inset-0 origin-left bg-gradient-to-r from-mango-600 via-mango-500 to-golden-500"
          style={{ scaleX: progressScaleX }}
        />
      </div>
    </motion.nav>
  );
});

// PERFORMANCE OPTIMIZATION ADDED - Display name for better debugging
Navbar.displayName = "Navbar";

export default Navbar;
